// Reduction-registry intent handlers (Stage 6).
//
// Four intents: create / update / delete / focus. Each mutation triggers a
// recompute against the current field array; the result lands in the
// module-level cache (src/reductions/cache.ts) and the UI subscribes there
// rather than via the store (results derive from spec + data, they are not
// independent state). Selection-registry mutations fan out to every
// reduction whose `domain` references the touched selection.

import { INTENTS } from '@store/intents';
import { params } from '@store/instance';
import {
    createReduction,
    reductionForDomain,
    reductionsReferencing,
    removeReduction,
    setFocus,
    updateReduction,
} from '@store/reductions';
import { reduce, type ReduceGrid } from '../../reductions/reduce';
import { setResult, clearResult } from '../../reductions/cache';
import { closeReductionPlot } from '../../ui/reductions/plotWindow';
import type { ReductionSpec } from '../../reductions/types';
import { resolveDomainBounds } from '@store/selections';
import type { ReductionId, State } from '@store/schema';
import type { Store } from '@store/store';
import type { ViewerContext } from '../context';

function gridFrom(viewer: ViewerContext): ReduceGrid {
    const g = viewer.grid;
    return {
        nx: g.nx,
        ny: g.ny,
        nz: g.nz,
        origin: [g.xMin, g.yMin, g.zMin] as const,
        spacing: [g.resolutionX, g.resolutionY, g.resolutionZ] as const,
    };
}

/** Resolve the field array for a Reduction.field name. */
function fieldArrayFor(viewer: ViewerContext, name: string): Float32Array | null {
    if (name === 'magnitude') return viewer.field.gridFlat;
    return viewer.field.data?.[name] ?? null;
}

/**
 * Run the reduce() pipeline for a single id. Cache-only side effects; the
 * UI rerenders via cache subscribers (`onResult`). No-op if the dataset is
 * not yet loaded, or if the requested field is missing.
 */
export function computeReduction(viewer: ViewerContext, id: ReductionId): void {
    const r = params.reductions[id];
    if (!r) return;
    if (viewer.grid.nx === 0) return; // dataset not loaded yet
    const field = fieldArrayFor(viewer, r.field);
    if (!field) {
        setResult(id, { kind: 'empty' });
        return;
    }
    const spec: ReductionSpec = {
        field: r.field,
        op: r.op,
        axes: r.axes,
        domain: r.domain,
    };
    const domain = resolveDomainBounds(params.selections, r.domain);
    const result = reduce(field, gridFrom(viewer), spec, domain);
    setResult(id, result);
}

/** Create + compute the reduction for a domain when none exists yet. */
function ensureDomainReduction(viewer: ViewerContext, domain: string): void {
    if (reductionForDomain(domain)) return;
    const id = createReduction({ domain, focus: false });
    computeReduction(viewer, id);
}

/** Recompute every active reduction. Called on dataset / field load. Seeds the
 *  always-present Full-domain reduction so the pane has something to show. */
export function recomputeAll(viewer: ViewerContext): void {
    ensureDomainReduction(viewer, 'full');
    for (const id of params.reductionOrder) computeReduction(viewer, id);
}

export function register(store: Store<State>, viewer: ViewerContext): Array<() => void> {
    const offs: Array<() => void> = [];

    offs.push(store.onIntent(INTENTS.REDUCTION_CREATE, (p) => {
        const seed = p?.seed ?? {};
        const id = createReduction({
            ...(seed.field ? { field: seed.field } : {}),
            ...(seed.op ? { op: seed.op } : {}),
            ...(seed.axes ? { axes: seed.axes } : {}),
            ...(seed.domain !== undefined ? { domain: seed.domain } : {}),
            ...(seed.timestepPolicy ? { timestepPolicy: seed.timestepPolicy } : {}),
            ...(typeof seed.label === 'string' ? { label: seed.label } : {}),
            ...(typeof p?.focus === 'boolean' ? { focus: p.focus } : {}),
        });
        computeReduction(viewer, id);
        store.dispatch(INTENTS.UI_PANE_REBUILD_SECTION, { which: 'reductions' });
    }));

    offs.push(store.onIntent(INTENTS.REDUCTION_UPDATE, (p) => {
        if (!p || !p.id || !p.patch) return;
        updateReduction(p.id, p.patch);
        computeReduction(viewer, p.id);
        // Label edits change the lineage breadcrumb; refresh the pane row.
        if (typeof p.patch.label === 'string') {
            store.dispatch(INTENTS.UI_PANE_REBUILD_SECTION, { which: 'reductions' });
        } else {
            store.dispatch(INTENTS.UI_PANE_REFRESH);
        }
    }));

    offs.push(store.onIntent(INTENTS.REDUCTION_DELETE, (p) => {
        if (!p || !p.id) return;
        removeReduction(p.id);
        clearResult(p.id);
        store.dispatch(INTENTS.UI_PANE_REBUILD_SECTION, { which: 'reductions' });
    }));

    offs.push(store.onIntent(INTENTS.REDUCTION_FOCUS, (p) => {
        const id = p && typeof p.id !== 'undefined' ? p.id : null;
        setFocus(id);
        store.dispatch(INTENTS.UI_PANE_REFRESH);
    }));

    // Selection-registry fan-out: when a selection's geometry changes or the
    // selection disappears, every reduction referencing it must recompute.
    offs.push(store.onIntent(INTENTS.SELECTION_UPDATE, (p) => {
        if (!p?.id) return;
        for (const id of reductionsReferencing(p.id)) computeReduction(viewer, id);
    }));
    offs.push(store.onIntent(INTENTS.SELECTION_DELETE, (p) => {
        if (!p?.id) return;
        // One-reduction-per-domain: the deleted selection's reduction is
        // deleted too (snapping its domain to 'full' would collide with the
        // Full domain's own reduction). selections.removeSelection already
        // snapped `reductionsDomain` → 'full', so the pane lands on Full.
        const orphans = reductionsReferencing(p.id);
        if (orphans.length === 0) return;
        for (const id of orphans) {
            removeReduction(id);
            clearResult(id);
            closeReductionPlot(id);
        }
        store.dispatch(INTENTS.UI_PANE_REBUILD_SECTION, { which: 'reductions' });
    }));

    // Dataset / field switch: recompute everything against the new data.
    offs.push(store.onIntent(INTENTS.SET_FIELD_COMPONENT, () => recomputeAll(viewer)));
    offs.push(store.onIntent(INTENTS.SWITCH_DATASET, () => recomputeAll(viewer)));

    return offs;
}
